import { TemplateControl } from '../request-result-modal.types';
import { ControlValueTranslator } from '../utils/control-value-translator.util';
import { LanguageMapper } from '../utils/language-mapper.util';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { ServerTranslateService } from '@services/server-translate.service';

/**
 * Service class for getting control values with proper formatting and translation
 */
export class ControlValueService {
  constructor(
    private dateFormatPipe: DateFormatLangPipe,
    private serverTranslate: ServerTranslateService,
  ) {}

  /**
   * Get formatted control value for display
   */
  getValue(control: TemplateControl, lang: string): string {
    let value = '';
    const resultValue = this.getResultValueAsString(control);

    // Priority 1: Use resultValues if exists and not empty
    if (resultValue && ControlValueTranslator.hasSemicolonSeparatedValues(control)) {
      const translatedValue = ControlValueTranslator.translateDropdownValue(control, resultValue, lang);
      if (translatedValue) {
        return translatedValue;
      }
    }

    const cropName = this.localizedCropName(control, lang);
    if (cropName) {
      return cropName;
    }

    if (resultValue) {
      value = resultValue;
    } else {
      value = ControlValueTranslator.getValueWithFallback(control, lang);
    }

    // If no value found anywhere, return default
    if (!value || value === '') {
      return '-';
    }

    // Format dates if needed (supports mixed enum formats: string/number/casing)
    if (this.isDateLikeControlType(control.type) && value) {
      const includeTime = this.isDateTimeLikeControlType(control.type);
      const formatted = this.dateFormatPipe.transform(value, lang, includeTime);
      return formatted || value;
    }

    return value;
  }

  /**
   * Get image path from control, resolving from extracted files if needed
   */
  getImagePath(control: TemplateControl, extractedFiles: Map<string, string | Blob>): string {
    const imageFilename = (control.values_uz || control.values_en || control.values_ru || this.getResultValueAsString(control)) as string;

    if (imageFilename && !imageFilename.startsWith('http') && !imageFilename.startsWith('blob:')) {
      const extractedFile = extractedFiles.get(imageFilename);
      if (extractedFile && typeof extractedFile === 'string') {
        return extractedFile;
      }
    }

    return imageFilename || '';
  }

  getImagePaths(control: TemplateControl, extractedFiles: Map<string, string | Blob>, maxCount = 5): string[] {
    const multiRaw = this.getResultValueAsString(control);
    if (multiRaw?.includes(';')) {
      const filenames = multiRaw.split(';').map(s => s.trim()).filter(Boolean).slice(0, maxCount);
      return filenames.map(fn => {
        if (!fn.startsWith('http') && !fn.startsWith('blob:')) {
          const extracted = extractedFiles.get(fn);
          if (extracted && typeof extracted === 'string') return extracted;
        }
        return fn;
      });
    }
    const single = this.getImagePath(control, extractedFiles);
    return single ? [single] : [];
  }

  /**
   * Convert control value for table display (e.g., checkbox to boolean)
   */
  getValueForTable(control: TemplateControl, lang: string): any {
    const value = this.getValue(control, lang);

    // Convert checkbox string values to boolean for table display
    if (control.type === 'checkbox') {
      return value === 'checked' || value === 'true';
    }

    return value;
  }

  private localizedCropName(control: TemplateControl, lang: string): string | null {
    const controlName = (control.name || '').toLowerCase();
    if (controlName !== 'crop' && controlName !== 'sowing_crop') {
      return null;
    }

    const descriptions = this.cropDescriptions(control);
    if (!descriptions) {
      return null;
    }

    const translated = this.serverTranslate.translateForLanguage(
      { descriptions },
      'descriptions',
      LanguageMapper.canonicalize(lang),
    );
    return translated?.trim() ? translated.trim() : null;
  }

  private cropDescriptions(control: TemplateControl): Record<string, unknown> | null {
    const raw = (control as { descriptions?: unknown }).descriptions;
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      return raw as Record<string, unknown>;
    }

    const legacy: Record<string, string> = {};
    if (control.descr_en?.trim()) {
      legacy['en-US'] = control.descr_en.trim();
    }
    if (control.descr_ru?.trim()) {
      legacy['ru-RU'] = control.descr_ru.trim();
    }
    if (control.descr_uz?.trim()) {
      legacy['uz-Latn-UZ'] = control.descr_uz.trim();
    }
    return Object.keys(legacy).length > 0 ? legacy : null;
  }

  private getResultValueAsString(control: TemplateControl): string {
    if (typeof control.resultValues !== 'string') {
      return '';
    }
    const text = control.resultValues.trim();
    if (text === '' || text === '-' || text === '[]' || text === 'null') {
      return '';
    }
    return control.resultValues;
  }

  private isDateLikeControlType(value: unknown): boolean {
    if (typeof value === 'number') {
      return value === 6 || value === 7;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'date' || normalized === 'datetime' || normalized === '6' || normalized === '7';
    }
    return false;
  }

  private isDateTimeLikeControlType(value: unknown): boolean {
    if (typeof value === 'number') {
      return value === 7;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'datetime' || normalized === '7';
    }
    return false;
  }
}

